import type { RefObject } from 'react';
import { useEffect } from 'react';

type IgnoreSelector = string | ((target: Element) => boolean);

/**
 * Calls the handler when a click is detected outside the referenced element.
 *
 * @param ref - React ref object pointing to the DOM element
 * @param handler - Function to call on outside click
 * @param ignoreSelectors - Optional array of CSS selectors or predicate functions to ignore clicks on
 */
export function useOnClickOutside<T extends HTMLElement>(
  ref: RefObject<T | null>,
  handler: (event: MouseEvent | TouchEvent) => void,
  ignoreSelectors?: IgnoreSelector[],
) {
  useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent) => {
      const el = ref?.current;
      const target = event.target as Element;

      // Do nothing if clicking ref's element or descendent elements
      if (!el || el.contains(target)) {
        return;
      }

      // Check if click should be ignored based on selectors
      if (ignoreSelectors) {
        for (const selector of ignoreSelectors) {
          if (typeof selector === 'string') {
            // CSS selector - check if target matches or is contained by matching element
            if (target.matches?.(selector) || target.closest?.(selector)) {
              return;
            }
          } else {
            // Predicate function
            if (selector(target)) {
              return;
            }
          }
        }
      }

      handler(event);
    };

    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);

    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler, ignoreSelectors]);
}
